import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import {
  REASON_TO_HIDE,
  RequiredVisibleInfo,
  REVEAL_AFTER,
} from 'models/Listing';
import { Field, Form } from 'react-final-form';
import ToggleField from 'components/ToggleField';
import RadioGroupField from 'components/RadioGroupField';
import InputField from 'components/InputField';
import validations from 'validations';
import { getFieldValidations } from 'validations/general';

import './RequiredVisibleForm.styles.scss';

export const REASON_TO_HIDE_COPY = {
  [REASON_TO_HIDE.HIDDEN_BY_BROKER]: 'hiddenByBroker',
  [REASON_TO_HIDE.AVAILABLE_UNDER_NDA]: 'availableUnderNDA',
  [REASON_TO_HIDE.CUSTOM]: 'customReason',
};

type RequiredVisibleFormProps = {
  className?: string;
  canChangeRequired: boolean;
  initialValues: RequiredVisibleInfo;
  onCancel: () => void;
  onSubmit: (values: RequiredVisibleInfo) => Promise<void>;
};

const RequiredVisibleForm: React.FC<RequiredVisibleFormProps> = (props) => {
  const {
    className,
    canChangeRequired,
    initialValues: propsInitialValues,
    onCancel,
    onSubmit,
  } = props;

  const classes = classNames('required-visible-form', className);

  const { t } = useTranslation();

  const reasonToHideOptions = useMemo(
    () => [
      {
        value: REASON_TO_HIDE.HIDDEN_BY_BROKER,
        label: t(REASON_TO_HIDE_COPY[REASON_TO_HIDE.HIDDEN_BY_BROKER]),
      },
      {
        value: REASON_TO_HIDE.AVAILABLE_UNDER_NDA,
        label: t(REASON_TO_HIDE_COPY[REASON_TO_HIDE.AVAILABLE_UNDER_NDA]),
      },
      {
        value: REASON_TO_HIDE.CUSTOM,
        label: t(REASON_TO_HIDE_COPY[REASON_TO_HIDE.CUSTOM]),
      },
    ],
    [t],
  );

  const revealAfterOptions = useMemo(
    () => [
      { value: REVEAL_AFTER.NEVER, label: t('neverKeptHidden') },
      { value: REVEAL_AFTER.AFTER_ENQUIRE, label: t('afterBuyerEnquires') },
      { value: REVEAL_AFTER.AFTER_NDA_SIGNED, label: t('afterNdaSigned') },
    ],
    [t],
  );

  const initialValues = useMemo(() => {
    if (!propsInitialValues.isVisible) return propsInitialValues;

    const { isRequired, isVisible } = propsInitialValues;

    return {
      isRequired,
      isVisible,
      reasonToHide: REASON_TO_HIDE.HIDDEN_BY_BROKER,
      revealAfter: REVEAL_AFTER.AFTER_ENQUIRE,
    };
  }, [propsInitialValues]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  return (
    <Form<RequiredVisibleInfo>
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({
        pristine,
        invalid,
        submitting,
        values: { isVisible, reasonToHide, customReasonToHide },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <div className="required-visible-form__fields">
            {canChangeRequired && (
              <Field
                name="isRequired"
                component={ToggleField}
                type="checkbox"
                className="black-toggle"
                label={t('requiredStatus')}
                onContent={<p>{t('required')}</p>}
                offContent={<p>{t('notRequired')}</p>}
              />
            )}

            <Field
              name="isVisible"
              component={ToggleField}
              type="checkbox"
              className="black-toggle"
              label={t('buyerVisibility')}
              onContent={<p>{t('visible')}</p>}
              offContent={<p>{t('hidden')}</p>}
            />

            {!isVisible && (
              <div className="fields-for-hidden">
                <RadioGroupField
                  name="reasonToHide"
                  label={t('buyerFacingReplacementText')}
                  options={reasonToHideOptions}
                  required
                />

                {reasonToHide === REASON_TO_HIDE.CUSTOM && (
                  <Field
                    name="customReasonToHide"
                    component={InputField}
                    placeholder={t('buyerFacingReplacementTextPlaceholder')}
                    {...getFieldValidations(true, [validations.maxChars(15)])}
                  />
                )}

                <RadioGroupField
                  name="revealAfter"
                  label={t('revealTrigger')}
                  options={revealAfterOptions}
                  required
                  description={t('revealAfterDescription', {
                    value:
                      reasonToHide === REASON_TO_HIDE.CUSTOM
                        ? customReasonToHide
                        : t(REASON_TO_HIDE_COPY[reasonToHide]),
                  })}
                />
              </div>
            )}
          </div>

          <div className="actions">
            <Button variant="link" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button type="submit" disabled={pristine || invalid || submitting}>
              {t('submit')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default RequiredVisibleForm;
